// Lightweight fetch helpers + a localStorage-backed retry queue for one-shot
// endpoints (leads, checkout-intent). The save-progress endpoint has its own
// dedup-by-snapshot logic in quizStore.ts and does NOT use this queue.
//
// Retry policy:
//   - 4xx responses are permanent failures (the server understood and said
//     no) → DROP from queue, no retry. Avoids endless loops on routes that
//     no longer exist or bodies that won't parse.
//   - 5xx + network errors are transient → keep in queue, retry on next
//     flushQueue() invocation, up to MAX_ATTEMPTS.
//   - Items older than MAX_AGE_MS get dropped regardless.

const QUEUE_KEY = 'jobtest-pending-api-v1';
const MAX_ATTEMPTS = 5;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface QueuedRequest {
  url: string;
  body: unknown;
  ts: number;
  /** Number of failed flushQueue attempts so far. */
  attempts?: number;
}

function getQueue(): QueuedRequest[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function setQueue(q: QueuedRequest[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (q.length === 0) {
      localStorage.removeItem(QUEUE_KEY);
    } else {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    }
  } catch {
    // Quota or privacy mode — silently drop. Dev tools will surface the
    // throw in the console.
  }
}

/** POST JSON. Returns parsed body on 2xx, null on failure. Only 5xx +
 *  network failures get queued for retry; 4xx is final. */
export async function postJson<T = unknown>(
  url: string,
  body: unknown,
): Promise<T | null> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) return (await res.json()) as T;
    // 4xx → caller's problem (validation, missing data, gone). Don't retry.
    if (res.status >= 400 && res.status < 500) return null;
    // 5xx → queue for later.
    setQueue([...getQueue(), { url, body, ts: Date.now(), attempts: 0 }]);
    return null;
  } catch {
    // Network error / DNS / offline.
    setQueue([...getQueue(), { url, body, ts: Date.now(), attempts: 0 }]);
    return null;
  }
}

/** Best-effort flush of queued requests. Drops items on 4xx, on >5
 *  attempts, or older than 7 days. */
export async function flushQueue(): Promise<void> {
  const q = getQueue();
  if (q.length === 0) return;
  const now = Date.now();
  const remaining: QueuedRequest[] = [];

  for (const item of q) {
    const attempts = (item.attempts ?? 0) + 1;
    if (now - item.ts > MAX_AGE_MS || attempts > MAX_ATTEMPTS) continue;

    try {
      const res = await fetch(item.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.body),
      });
      if (res.ok) continue;            // success → drop from queue
      if (res.status >= 400 && res.status < 500) continue; // permanent → drop
      remaining.push({ ...item, attempts });               // 5xx → keep
    } catch {
      remaining.push({ ...item, attempts });               // network → keep
    }
  }
  setQueue(remaining);
}

/** Manual escape hatch — clears the queue without firing requests. Useful
 *  for dev console resets. Exported so it can be called from the bypass
 *  snippets without poking at internals. */
export function clearApiQueue(): void {
  setQueue([]);
}
